import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Truck, Check, AlertCircle, X, Loader,
  ShoppingCart, ExternalLink, User, Phone, MapPin
} from 'lucide-react';
import '../Boutiques.css';

export default function EcommerceCommandes() {
  const [boutiques,    setBoutiques]    = useState([]);
  const [boutiqueId,   setBoutiqueId]   = useState(''); // '' = toutes
  const [orders,       setOrders]       = useState([]);
  const [ordersStatut, setOrdersStatut] = useState('');
  const [loading,      setLoading]      = useState(true);
  const [toast,        setToast]        = useState({ type: '', text: '' });

  useEffect(() => { init(); }, []); // eslint-disable-line
  useEffect(() => { loadOrders(); }, [boutiqueId, ordersStatut]); // eslint-disable-line

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast({ type: '', text: '' }), 4000);
  };

  const init = async () => {
    try {
      const { data } = await axios.get('/api/boutiques');
      setBoutiques(data);
    } catch { /* silencieux */ }
  };

  const shopUrl = (id) => `${window.location.origin}/shop/${id}`;

  const loadOrders = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/commandes-en-ligne', {
        params: { boutique: boutiqueId || undefined, statut: ordersStatut || undefined }
      });
      setOrders(data.data || []);
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur chargement commandes');
    } finally { setLoading(false); }
  };

  const validerCommande = async (cmd) => {
    if (!window.confirm(`Valider la commande ${cmd.numeroCommande} ? Le stock sera décrémenté.`)) return;
    try {
      await axios.post(`/api/commandes-en-ligne/${cmd._id}/valider`);
      showToast('success', 'Commande validée');
      loadOrders();
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur lors de la validation');
    }
  };

  const livrerCommande = async (cmd) => {
    if (!window.confirm(`Marquer ${cmd.numeroCommande} comme livrée et payée ?`)) return;
    try {
      await axios.post(`/api/commandes-en-ligne/${cmd._id}/livrer`);
      showToast('success', 'Commande livrée');
      loadOrders();
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur');
    }
  };

  const annulerCommande = async (cmd) => {
    const motif = window.prompt(`Motif d'annulation pour ${cmd.numeroCommande} ?`, '');
    if (motif === null) return;
    try {
      await axios.post(`/api/commandes-en-ligne/${cmd._id}/annuler`, { motif });
      showToast('success', 'Commande annulée');
      loadOrders();
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur');
    }
  };

  return (
    <div className="bt-page">
      {toast.text && (
        <div className={`bt-toast bt-toast--${toast.type}`}>
          {toast.type === 'success' ? <Check size={14}/> : <AlertCircle size={14}/>}
          {toast.text}
        </div>
      )}

      <div className="bt-header">
        <div className="bt-header-left">
          <div className="bt-header-icon"><Truck size={20}/></div>
          <div>
            <h1 className="bt-header-title">Commandes en ligne</h1>
            <p className="bt-header-sub">Gérez les commandes reçues depuis vos vitrines (paiement à la livraison)</p>
          </div>
        </div>
      </div>

      <div className="bt-content">
        {/* Filtres */}
        <div className="bt-orders-filters" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <select className="bt-select" value={boutiqueId} onChange={e => setBoutiqueId(e.target.value)}
            style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}>
            <option value="">Toutes les boutiques</option>
            {boutiques.map(b => <option key={b._id} value={b._id}>{b.nom}</option>)}
          </select>
          {[
            ['',          'Toutes'],
            ['en_attente','En attente'],
            ['confirmee', 'Confirmées'],
            ['livree',    'Livrées'],
            ['annulee',   'Annulées'],
          ].map(([k, lbl]) => (
            <button key={k}
              className={`bt-orders-tab ${ordersStatut === k ? 'bt-orders-tab--active' : ''}`}
              onClick={() => setOrdersStatut(k)}>
              {lbl}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="bt-orders-loading"><Loader size={18} className="bt-spin"/> Chargement…</div>
        ) : orders.length === 0 ? (
          <div className="bt-orders-empty">
            <ShoppingCart size={32}/>
            <p>Aucune commande{ordersStatut ? ' pour ce statut' : ''}.</p>
          </div>
        ) : (
          <div className="bt-orders-list">
            {orders.map(cmd => {
              const isPending = cmd.statut === 'en_attente';
              const isConfirmed = cmd.statut === 'confirmee';
              const dt = new Date(cmd.createdAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
              const bId = cmd.boutiqueId?._id || cmd.boutiqueId;
              const bNom = cmd.boutiqueId?.nom;
              return (
                <div key={cmd._id} className={`bt-order bt-order--${cmd.statut}`}>
                  <div className="bt-order-head">
                    <div>
                      <div className="bt-order-num">{cmd.numeroCommande}</div>
                      <div className="bt-order-date">
                        {dt}
                        {bNom && <> · {bNom}</>}
                        {bId && (
                          <> · <a href={shopUrl(bId)} target="_blank" rel="noreferrer" className="bt-shop-link">
                            vitrine <ExternalLink size={10}/>
                          </a></>
                        )}
                      </div>
                    </div>
                    <span className={`bt-order-badge bt-order-badge--${cmd.statut}`}>
                      {cmd.statut === 'en_attente'  && 'En attente'}
                      {cmd.statut === 'confirmee'   && 'Confirmée'}
                      {cmd.statut === 'livree'      && 'Livrée'}
                      {cmd.statut === 'annulee'     && 'Annulée'}
                    </span>
                  </div>

                  <div className="bt-order-client">
                    <div><User size={11}/> <strong>{cmd.client?.nom}</strong></div>
                    <div><Phone size={11}/> {cmd.client?.telephone}</div>
                    <div><MapPin size={11}/> {cmd.client?.adresse}</div>
                    {cmd.client?.commentaire && <div className="bt-order-note">{cmd.client.commentaire}</div>}
                  </div>

                  <div className="bt-order-items">
                    {cmd.details.map((d, i) => (
                      <div key={i} className="bt-order-item">
                        <span>{d.nomProduit} × {d.quantite}</span>
                        <span>{new Intl.NumberFormat('fr-FR').format(d.sousTotal)} XOF</span>
                      </div>
                    ))}
                    <div className="bt-order-total">
                      <span>Total à régler à la livraison</span>
                      <strong>{new Intl.NumberFormat('fr-FR').format(cmd.total)} XOF</strong>
                    </div>
                  </div>

                  <div className="bt-order-actions">
                    {isPending && (
                      <>
                        <button className="bt-order-btn bt-order-btn--valid" onClick={() => validerCommande(cmd)}>
                          <Check size={12}/> Valider
                        </button>
                        <button className="bt-order-btn bt-order-btn--cancel" onClick={() => annulerCommande(cmd)}>
                          <X size={12}/> Refuser
                        </button>
                      </>
                    )}
                    {isConfirmed && (
                      <>
                        <button className="bt-order-btn bt-order-btn--deliver" onClick={() => livrerCommande(cmd)}>
                          <Truck size={12}/> Marquer livrée & payée
                        </button>
                        <button className="bt-order-btn bt-order-btn--cancel" onClick={() => annulerCommande(cmd)}>
                          <X size={12}/> Annuler
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
